/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import Body from '../static/Body';
import Card from '../static/Card';
import { motion } from 'framer-motion';
import ImageUploader from '../elements/ImageUploader';
import InputField from '../elements/InputField';
import CardRow from '../static/CardRow';
import Text from '../elements/TextComponent';
import DocumentUploader from '../elements/DocumentUploader';
import ExperienceInput, { Experience } from '../elements/ExperienceInput';
import HighlightsComponent from '../static/HighlightsComponent';
import { CreateAssistantPayload } from '../../api/types';
import { createResumeAssistant } from '../../api/assistantsApi';
import QuestionChipsInput from '../static/QuestionChipInput';
import Button from '../elements/Button';
import LoaderOverlay from '../static/LoaderOverlay';
import { useProduct } from '../../context/ProductContext';

// The shape HighlightsComponent wants for each experience block
type HLExperience = {
  company: string;
  years: string;
  sentences: string[];
};

interface Props {
  goToAssistants: () => void;
}

const CreateAssistant: React.FC<Props> = ({goToAssistants}) => {
  const { product } = useProduct();
  
  const handleViewAssistants = () => {
      goToAssistants(); // Bubble up to Dashboard
  };

  const [selectedFilesFromDocumentUploader, setSelectedFilesFromDocumentUploader] = useState<File[]>([]);

  const [submittedExperiences, setSubmittedExperiences] = useState<HLExperience[]>([]);
  const [allKeywords, setAllKeywords] = useState<string[]>([]); // <-- separated keywords state

  // (Optional) if you also want to keep the raw data you can keep it here for server submit:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rawExperiences, setRawExperiences] = useState<Experience[]>([]);

  const [location, setLocation] = useState('');
  const [locationError, setLocationError] = useState(false);
    
  const [languages, setLanguages] = useState('');
  const [languagesError, setLanguagesError] = useState(false);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  

  const [profileImage, setProfileImage] = useState('')

  const [focus, setFocus] = useState('')
  const [focusError, setFocusError] = useState(false);

  const [questions, setQuestions] = useState<string[]>([
  ]);

  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);

  const parseLanguages = (input: string) => {
    return input
      .split(/[,\s]+/)        // split on commas OR any whitespace (one or more)
      .map(s => s.trim())
      .filter(Boolean);       // remove empty strings
  }
  const handleExperienceSubmit = (exp: Experience) => {
    // Clean braces from sentences
    const cleanedSentences = exp.entries.map((e) =>
        e.sentence.replace(/\{([^}]+)\}/g, '$1') // remove { and }
    );

    // Convert Experience -> HighlightsComponent's experience block
    const grouped: HLExperience = {
        company: exp.company,
        years: `${exp.startYear}–${exp.endYear}`,
        sentences: cleanedSentences,
    };

    // Merge keywords
    const newKeywords = Array.from(
        new Set([
        ...allKeywords,
        ...exp.entries.flatMap((e) => e.keywords),
        ])
    );

    setSubmittedExperiences((prev) => [...prev, grouped]);
    setAllKeywords(newKeywords);
    setRawExperiences((prev) => [
        ...prev,
        {
        ...exp,
        entries: exp.entries.map((e) => ({
            ...e,
            sentence: e.sentence.replace(/\{([^}]+)\}/g, '$1'), // store cleaned sentences
        })),
        },
    ]);
    };

  const handleFinish = async () => {
    if (name === '') {
      setNameError(true)
      setStatus('error')
      setMessage('All fields must be filled')
      return;
    } else {
      setNameError(false)
    }
    if (focus === '') {
      setFocusError(true)
      setStatus('error')
      setMessage('All fields must be filled')
      return;
    } else {
      setFocusError(false)
    }
    if (location === '') {
      setLocationError(true)
      setStatus('error')
      setMessage('All fields must be filled')
      return;
    } else {
      setLocationError(false)
    }
    if (languages === '') {
      setLanguagesError(true)
      setStatus('error')
      setMessage('All fields must be filled')
      return;
    } else {
      setLanguagesError(false)
    }
    if (questions.length === 0) {
      setStatus('error')
      setMessage('Add at least one question for your assistant!')
      return;
    }
    if (profileImage === '') {
      setStatus('error')
      setMessage('You must upload an image for your assistant!')
      return;
    } else {
      setStatus('idle')
      setMessage('')
    }
    if (selectedFilesFromDocumentUploader.length === 0) {
      setStatus('error')
      setMessage('You must upload at least one file for your assistant!')
      return;
    }
    setLoading(true)
    const payload: CreateAssistantPayload = {
      name,
      location,
      spokenLanguages: parseLanguages(languages),
      keywords: allKeywords,
      experiences: submittedExperiences,
      files: selectedFilesFromDocumentUploader,
      profileImage: profileImage,
      focus: focus,
      questions: questions,
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { data, error } = await createResumeAssistant(payload);
    setLoading(false)
    if (error) {
      // toast error
      setStatus('error')
      setMessage('Filed to create Assistant')
      return;
    } else {
      handleViewAssistants()
    }
    // success toast / navigate
};

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3 }}
      >
        <div style={{ overflowY: 'auto', height: '100vh' }}>
          <LoaderOverlay show={loading} size={18} color="#ffffff" />
          <Body>
            <Card width="92%">
                <Text
                  text="Create a Resume Assistant"
                  fontSize="26px"
                  fontWeight="700"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <div className='margin-top-10'></div>
                <Text
                  text="The AI Assistant will train itself on your uploaded documents only"
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
              <Text
                  text="Add an image of yourself, your name, experience and your resume for displaying to potential recuiters."
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <Text
                  text="Recruiters focus on keywords in your experience, adding them below and in {} will automatically highlight them."
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <Text
                  text="It is recommended to have a resume assistant per Resume focus e.g. one for Backend another for FullStack jobs"
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <Text
                  text="When you are finished, the Finish and Submit buttom is at the very bottom of the page."
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
            </Card>
            <Card width="92%">
                <Text
                  text="Focus"
                  fontSize="18px"
                  fontWeight="700"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <div className="margin-top-10">
                  <Text
                  text="A focus represents the type of job this assistant will be made for"
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <Text
                  text="Used for your reference when viewing your assistants"
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <InputField
                    text="e.g. Backend, Help-desk..."
                    fontSize="16px"
                    width="100%"
                    fontWeight={500}
                    error={focusError}
                    onChange={(e) => setFocus(e.target.value)}
                  />
                </div>
              </Card>
            <CardRow>
              <Card width="27%">
                <ImageUploader
                  onUploadSuccess={(imageUrl) => {
                    setProfileImage(imageUrl); // store in parent state
                  }}
                  size={150}
                  maxSizeMB={3}
                  showSpinner={true}
                />
                <div className="margin-top-30">
                  <InputField
                    text="Name your Assistant"
                    fontSize="16px"
                    width="100%"
                    fontWeight={500}
                    error={nameError}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </Card>

              <Card width="30%">
                <Text
                  text="Location"
                  fontSize="18px"
                  fontWeight="700"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <div className="margin-top-30 margin-bottom-15">
                  <InputField
                    text="Enter your location"
                    fontSize="16px"
                    width="100%"
                    fontWeight={500}
                    error={locationError}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>
                <Text
                  text="Spoken Languages"
                  fontSize="18px"
                  fontWeight="700"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <div className="margin-top-30">
                  <InputField
                    text="Enter the languages you speak"
                    fontSize="16px"
                    width="100%"
                    fontWeight={500}
                    error={languagesError}
                    onChange={(e) => setLanguages(e.target.value)}
                  />
                </div>
              </Card>

              <Card width="30%">
                <Text
                  text="Upload Documents"
                  fontSize="18px"
                  fontWeight="700"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <div className="margin-top-30">
                  <DocumentUploader
                    onFilesChange={setSelectedFilesFromDocumentUploader}
                  />
                </div>
              </Card>
            </CardRow>
            <Card width="92%">
                <Text
                  text="Questions"
                  fontSize="18px"
                  fontWeight="700"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <div className="margin-top-10">
                  <Text
                  text="Questions you create here will float above the chat input and when selected will be answered by the assistant."
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <Text
                  text="They give a starting point or sample questions which you or the recruiter may want to ask the assistant"
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <Text
                  text="Two are displayed at a time and the next question in the queue will appear once a question is selected"
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="28px"
                  color="#030303"
                  textAlign="left"
                />
                <QuestionChipsInput
                  questions={questions}
                  setQuestions={setQuestions}
                  placeholder="Type a question and press Enter…"
                  preventDuplicates
                  // maxItems={10}
                />
                </div>
              </Card>
            <Card width="92%">

            
              
              <Text
                text="Experience"
                fontSize="18px"
                fontWeight="700"
                lineHeight="28px"
                color="#030303"
                textAlign="left"
              />
              <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '16px',          // optional spacing between children
                    alignItems: 'center', // vertically center items
                    justifyContent: 'flex-start', // align items to the left
                    width: '100%',
                }}
                >
                <div className="margin-top-30">
                    <ExperienceInput onSubmit={handleExperienceSubmit} />
                </div>

                <div style={{ marginTop: 32 }}>
                    <HighlightsComponent
                    minWidth='700px'
                    location={location}
                    spokenLanguages={parseLanguages(languages)}
                    keywords={allKeywords}                 // <-- pass keywords separately
                    experiences={submittedExperiences}      // <-- just the display data
                    />
                </div>
            </div>
            </Card>
            {status !== 'idle' && (
              <Card height='90px' width="92%">
                <p
                    style={{
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    marginTop: '20px',
                    color: status === 'success' ? '#2e7d32' : '#d32f2f',
                    textAlign: 'center',
                    }}
                >
                    {message}
                </p>
                </Card>
                )}
            <Card height='200px' width="92%">
                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                onClick={handleFinish}
                width='100%'
                label='Finish & Submit'
                ></Button>
                {/* <button
                  onClick={handleFinish}
                  style={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: '#8b2cd7',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '20px 26px',
                    fontSize: "16px",
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Finish & Submit
                </button> */}
              </div>
              </Card>
          </Body>
        </div>
      </motion.div>

      {/* <SummarySidebar /> */}
    </>
  );
};

export default CreateAssistant;
